import { useCallback, useRef } from 'react';
import {
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  Button,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { InputPhone } from '@/components/ControlledInput';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { useGetIndicationMapAddressByPostalCode, usePostIndicationMapMutation } from '@/services/queries/location';
import { PostIndicationMapRequest } from '@/services/request/location';
import { ImageUpload } from '@/components/ImageUpload';

export interface IndicationMapCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const IndicationMapCreateModal = ({ isOpen, onClose, onDone }: IndicationMapCreateModalProps) => {
  const toast = useToast();
  const { t } = useTranslation();

  const postItemMutation = usePostIndicationMapMutation();
  const { mutate: fetchAddress } = useGetIndicationMapAddressByPostalCode();

  const {
    handleSubmit,
    register,
    setValue,
    control,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      description: '',
      image: '',
      address: {
        postal_code: '',
        line1: '',
        line2: '',
        line3: '',
        neighborhood: '',
        city: '',
        state: '',
        country_code: '',
      },
      latitude: undefined,
      longitude: undefined,
      phone_number: '',
      email: '',
      website: '',
      instagram: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const handlePostalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const postalCode = e.target.value?.replace(/\D/g, '');
      if (postalCode.length === 8) {
        fetchAddress(postalCode, {
          onSuccess: data => {
            if (data) {
              setValue('address.line1', data.line1 || '');
              setValue('address.neighborhood', data.neighborhood || '');
              setValue('address.city', data.city || '');
              setValue('address.state', data.state || '');
              setValue('address.country_code', data.country_code || 'BR');
              if (data.latitude) setValue('latitude', data.latitude);
              if (data.longitude) setValue('longitude', data.longitude);
            }
          },
        });
      }
    },
    [fetchAddress, setValue],
  );

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostIndicationMapRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        title: data.title,
        image: data.image,
        description: data.description ?? '',
        address: {
          postal_code: data.address?.postal_code ?? '',
          line1: data.address?.line1 ?? '',
          line2: data.address?.line2 ?? '',
          line3: data.address?.line3 ?? '',
          neighborhood: data.address?.neighborhood ?? '',
          city: data.address?.city ?? '',
          state: data.address?.state ?? '',
          country_code: data.address?.country_code ?? '',
        },
        phone_number: data.phone_number ?? '',
        email: data.email ?? '',
        website: data.website ?? '',
        instagram: data?.instagram ?? '',
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_indications_map.components.location_create_modal.location_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_indications_map.components.location_create_modal.location_create_error'),
          });
        },
      });
    },
    [postItemMutation, onDone, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_indications_map.components.location_create_modal.location_create_title')}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_indications_map.components.location_create_modal.location_create_team_label')}
              placeholder={t('pages.application_indications_map.components.location_create_modal.location_create_team_label')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_create_modal.location_create_title_label')}
              {...register('title')}
              helperText={t('pages.application_indications_map.components.location_create_modal.location_create_title_helper')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputPhone
              withoutFloating
              name='phone_number'
              control={control}
              label={t('pages.application_indications_map.components.location_create_modal.location_create_phone')}
              rootProps={{ isInvalid: !!errors?.phone_number, errorMessage: errors?.phone_number?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_create_modal.location_create_email')}
              {...register('email')}
              rootProps={{ isInvalid: !!errors?.email, errorMessage: errors?.email?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_create_modal.location_create_website')}
              {...register('website')}
              rootProps={{ isInvalid: !!errors?.website, errorMessage: errors?.website?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_create_modal.location_create_instagram')}
              {...register('instagram')}
              rootProps={{ isInvalid: !!errors?.instagram, errorMessage: errors?.instagram?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_create_modal.location_create_postal_code')}
              {...register('address.postal_code')}
              onChange={handlePostalCodeChange}
              rootProps={{ isInvalid: !!errors?.address?.postal_code, errorMessage: errors?.address?.postal_code?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_indications_map.components.location_create_modal.location_create_address_line1')}
              {...register('address.line1')}
              rootProps={{ isInvalid: !!errors?.address?.line1, errorMessage: errors?.address?.line1?.message }}
            />

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_create_modal.location_create_address_line2')}
                {...register('address.line2')}
                rootProps={{ isInvalid: !!errors?.address?.line2, errorMessage: errors?.address?.line2?.message }}
                flex='1'
              />
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_create_modal.location_create_address_line3')}
                {...register('address.line3')}
                rootProps={{ isInvalid: !!errors?.address?.line3, errorMessage: errors?.address?.line3?.message }}
                flex='1'
              />
            </Flex>

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_create_modal.location_create_neighborhood')}
                {...register('address.neighborhood')}
                rootProps={{ isInvalid: !!errors?.address?.neighborhood, errorMessage: errors?.address?.neighborhood?.message }}
                flex='1'
              />
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_create_modal.location_create_city')}
                {...register('address.city')}
                rootProps={{ isInvalid: !!errors?.address?.city, errorMessage: errors?.address?.city?.message }}
                flex='1'
              />
            </Flex>

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_create_modal.location_create_state')}
                {...register('address.state')}
                helperText={t('pages.application_indications_map.components.location_create_modal.location_create_state_helper')}
                maxLength={2}
                rootProps={{ isInvalid: !!errors?.address?.state, errorMessage: errors?.address?.state?.message }}
                flex='1'
              />
              <InputField
                withoutFloating
                label={t('pages.application_indications_map.components.location_create_modal.location_create_country_code')}
                {...register('address.country_code')}
                rootProps={{ isInvalid: !!errors?.address?.country_code, errorMessage: errors?.address?.country_code?.message }}
                flex='1'
              />
            </Flex>

            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                type='number'
                step='any'
                label={t('pages.application_indications_map.components.location_create_modal.location_create_latitude')}
                {...register('latitude')}
                rootProps={{
                  isInvalid: !!errors?.latitude,
                  errorMessage: errors?.latitude?.message,
                }}
                flex='1'
              />
              <InputField
                withoutFloating
                type='number'
                step='any'
                label={t('pages.application_indications_map.components.location_create_modal.location_create_longitude')}
                {...register('longitude')}
                rootProps={{
                  isInvalid: !!errors?.longitude,
                  errorMessage: errors?.longitude?.message,
                }}
                flex='1'
              />
            </Flex>

            <ImageUpload
              imageURL={''}
              onFileChange={image => setValue('image', image)}
              error={errors?.image?.message?.toString()}
            />
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='space-between' gap='kilo'>
          <Flex w='100%' justifyContent='space-between' alignItems='center' gap={2} paddingLeft={6}>
            <Flex w='100%' justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_indications_map.components.location_create_modal.location_create_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_indications_map.components.location_create_modal.location_create_create')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
